import makeStyles from '@mui/styles/makeStyles';
import { ReactElement, useState } from 'react';
import { RaptorTheme, mainColors } from '../../../../../../styling/theme';
import client from '../../../../../../utilities/requestClient';
import { BASE_URL } from '../../../../../../utilities/apiConstants';
import { FunctionalityHandlerProps } from '../../Functionality';
import FunctionalityButton from '../functionalityButton';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import GridItem from '../../../../../layout/GridComponents/GridItem';
import {
  calculateQuarter,
  getAvailableDateXMonthsAgo,
  getMostRecentAvailableDate,
} from '../../../../../../utilities/dateUtilities';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import {
  activeSectionSelector,
  createSectionByIdSelector,
  createSectionFundsSelector,
} from '../../../../../../redux/pages/selectors';
import GeneralMultiSelect from '../../../../../selects/GeneralMultiSelect.component';
import saveAs from 'file-saver';
import { AxiosError } from 'axios';
import { CircularProgress, Typography } from '@mui/material';
import RaptorDatePicker from '../../../../../selects/RaptorDatePicker.component';
import { getAllRelevantDates } from '../../../../../../utilities/dateUtilities';


const useStyles = makeStyles<RaptorTheme>((theme) => ({
  fdiContainer: {
    display: 'flex',
    position: 'relative',
    top: '0.62rem',
  },
  pickerRoot: {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
    height: '3.75rem',
    width: '18.75rem',
  },
  header: {
    position: 'relative',
    // top: '0.31rem',
  },
  feedback: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  loadingMessage: {
    color: mainColors.mainBlue,
  },
  errorMessage: {
    color: mainColors.Fail,
  },
  successMessage: {
    color: mainColors.Pass,
  },
}));

function CounterpartyReport(props: FunctionalityHandlerProps): ReactElement {
  const classes = useStyles();

  // Get a list of the available funds
  const section = useSelector(activeSectionSelector);
  const fundDetails = useSelector(
    createSectionFundsSelector(section || 'ucits'),
  );
  const fundIds = fundDetails != null ? fundDetails.map(({ id }) => id) : [];
  const fundNames =
    fundDetails != null ? fundDetails.map(({ name }) => name) : [];

  const [funds, setFunds] = useState<string[]>(fundIds);

  // get available dates (just take overview dates for now) maybe in future choose dates based on the funds selected?
  const [availableDates] = useState<string[]>(
    props.availableDates?.overview || [],
  );

  // End date for running the report (default to today)
  const [startDate, setStartDate] = useState<string>(
    getAvailableDateXMonthsAgo(1, availableDates),
  );
  const [endDate, setEndDate] = useState<string>(
    getMostRecentAvailableDate(availableDates),
  );

  // Create a function for generating the url and calling the report when the button is pressed
  const sectionDetails = useSelector(
    createSectionByIdSelector(section || 'ucits'),
  );
  const fundTypes = sectionDetails?.fundTypes;

  const schemas = sectionDetails?.fundTypes.length
    ? `?schemas=${sectionDetails?.fundTypes}`
    : '';
  const middle =
    fundTypes?.length && funds.length ? `&` : funds.length ? '?' : '';
  const fundsForParam = funds.length ? `fund_names=${funds}` : '';

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageStatus, setMessageStatus] = useState<
    'success' | 'error' | 'loading'
  >('success');
  const handleRequest = () => {
    const url =
      `${BASE_URL}bespoke_reporting/counterparty_report/${startDate}/${endDate}` +
      schemas +
      middle +
      fundsForParam;
    const requestClient = client();
    setLoading(true);
    setMessageStatus('loading');
    setMessage('Generating Report...');
    requestClient
      .get(url, {
        responseType: 'blob',
      })
      .then((response: any) => {
        const blob = response.data;
        // Goal: download the blob as a file and name it with the filename from the response header
        // The issue is that axios is not exposing the content-disposition header
        // will name the file manually for now and try resolve that issue later
        // const filename = response.headers['content-disposition']
        //   .split(';')
        //   .find((n: string) => n.includes('filename='))
        //   .replace('filename=', '')
        //   .trim()
        // ;
        // saveAs(blob, filename);
        saveAs(
          blob,
          `${calculateQuarter(endDate)} ${dayjs(
            endDate,
          ).year()} Counterparty Data.zip`,
        );

        setLoading(false);
        setMessageStatus('success');
        setMessage('Report Generated!');
      })
      .catch((error: AxiosError) => {
        setLoading(false);
        setMessageStatus('error');
        if (error.response?.status) {
          setMessage('Selected funds not found within the specified range.');
        } else {
          setMessage('Failed to generate report.');
        }
      })
      .finally(() => {
        setTimeout(() => {
          setMessageStatus('loading');
          setMessage('');
        }, 10000);
      });
  };

  return (
    <GridItem
      card
      xs={props.size}
      cardStyle={{ height: '100%', padding: '1.25rem' }}
    >
      <div className={classes.header}>
        <Typography
          variant="h2"
          style={{
            fontSize: '1.50rem',
            width: '100%',
            fontWeight: 500,
            textAlign: 'left',
          }}
          className={classes.header}
        >
          {props.cardTitle}
        </Typography>
      </div>
      <div className={classes.fdiContainer}>
        <RaptorDatePicker
          key={'start-date'}
          title={'Start Date: '}
          handler={(val) => {
            setStartDate(dayjs(val).format('YYYY-MM-DD'));
          }}
          datesToInclude={getAllRelevantDates()}
          selectedDate={dayjs(startDate)}
          tiedToActiveDate={false}
        />
        <RaptorDatePicker
          // key={picker.titleBarKey}
          title={'End Date: '}
          handler={(val) => setEndDate(dayjs(val).format('YYYY-MM-DD'))}
          datesToInclude={getAllRelevantDates()}
          selectedDate={dayjs(endDate)}
          tiedToActiveDate={false}
        />
        <GeneralMultiSelect
          labels={fundNames}
          values={fundIds}
          selected={funds}
          setSelected={setFunds}
          placeholderValue={'None Selected'}
          placeholderColor={mainColors.mainBlue}
          selectTitle={'Select funds'}
          customStyles={{
            pickerRoot: classes.pickerRoot,
          }}
        />
        <FunctionalityButton
          handler={handleRequest}
          title="Run Report"
          height="3.75rem"
        />
        <div className={classes.feedback}>
          <div
            className={
              messageStatus === 'success'
                ? classes.successMessage
                : messageStatus === 'error'
                  ? classes.errorMessage
                  : classes.loadingMessage
            }
          >
            {loading && (
              <CircularProgress size={20} style={{ marginRight: '0.62rem' }} />
            )}
            {message}
          </div>
        </div>
      </div>
    </GridItem>
  );
}

export default CounterpartyReport;
